import { css } from '@emotion/css'
import { Popover } from '@mantine/core'
import type { DatePickerProps, DateStringValue } from '@mantine/dates'
import { DatePicker } from '@mantine/dates'
import { useClickOutside, useViewportSize } from '@mantine/hooks'
import { ModalDrawer } from 'components/ModalDrawer/ModalDrawer'
import moment from 'moment-timezone'
import { useState } from 'react'
import { colors } from 'settings/colors'
import { SharedSettings } from 'settings/SharedSettings'
import type { DateRangePickerDropdownProps } from './types'

// Mounted only while the dropdown is open, so the shown month starts on the selected range every time
const DateRangePickerCalendar = (
  props: DateRangePickerDropdownProps & { thereIsRoomForDropdown: boolean },
) => {
  const [shownDate, setShownDate] = useState<DateStringValue | Date>(props.value[0] ?? new Date())

  const containerRef = useClickOutside(() => {
    if (props.thereIsRoomForDropdown) props.dismiss()
  })

  const dayStringToLocalDate = (day: DateStringValue) => moment.tz(day, moment.tz.guess()).toDate()

  return (
    <div ref={containerRef} css={{ maxWidth: '100%' }}>
      <DatePicker<'range'>
        allowSingleDateInRange={props.allowSingleDateInRange}
        date={shownDate}
        defaultValue={props.defaultValue}
        maxDate={props.maxDate}
        minDate={props.minDate}
        onChange={(dates) => {
          if (dates[0]) {
            props.setValue([dayStringToLocalDate(dates[0]), dates[1] ? dayStringToLocalDate(dates[1]) : null])
          }
        }}
        onDateChange={setShownDate}
        renderDay={(day) => props.renderDayWithValue(dayStringToLocalDate(day), props.value)}
        classNames={props.classNames as DatePickerProps<'range'>['classNames']}
        styles={props.styles as DatePickerProps<'range'>['styles']}
        type="range"
        value={props.value}
        size={props.thereIsRoomForDropdown ? 'sm' : 'lg'}
        css={{ display: 'flex', justifyContent: 'center', maxWidth: '100%' }}
      />
      <div
        css={{
          borderTop: `1px solid ${colors.grey500}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          marginTop: '10px',
          width: '100%',
        }}
      >
        {props.Footer({
          value: props.value,
          handleValueChange: (range) => {
            if (range[0]) setShownDate(range[0])

            props.setValue(range)
          },
          setDropdownOpened: props.setDropdownOpened,
        })}
      </div>
    </div>
  )
}

export const DateRangePickerDropdown = (props: DateRangePickerDropdownProps) => {
  const viewport = useViewportSize()

  const thereIsRoomForDropdown =
    viewport.height > SharedSettings.MINIMUM_VIEWPORT_HEIGHT_FOR_DATE_RANGE_PICKER_DROPDOWN

  const dropDownChildren = (
    <DateRangePickerCalendar {...props} thereIsRoomForDropdown={thereIsRoomForDropdown} />
  )

  return thereIsRoomForDropdown ? (
    <Popover transitionProps={{ transition: 'slide-down' }} position="bottom">
      <div
        css={{
          border: props.dropDownOpened ? `1px solid ${colors.grey200}` : 'unset',
          borderRadius: 3,
          overflowY: 'hidden',
          boxShadow: props.dropDownOpened ? `2px 2px 2px ${colors.grey200}` : 'unset',
        }}
      >
        {props.dropDownOpened && (
          <div
            css={{
              alignItems: 'center',
              justifyContent: 'center',
              display: 'flex',
              flexDirection: 'column',
              padding: '10px 10px 10px',
              background: colors.white,
            }}
          >
            {dropDownChildren}
          </div>
        )}
      </div>
    </Popover>
  ) : (
    <ModalDrawer
      opened={props.dropDownOpened}
      onClose={() => props.dismiss()}
      zIndex={SharedSettings.DEFAULT_MODAL_DRAWER_Z_INDEX}
    >
      <div
        className={css({
          justifyContent: 'center',
          alignSelf: 'center',
          justifySelf: 'center',
          display: 'flex',
          minHeight: 550,
          padding: '0 12px 12px 12px',
        })}
      >
        {dropDownChildren}
      </div>
    </ModalDrawer>
  )
}
